import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { useDovizKurlari } from '@/hooks/useDovizKurlari';
import type { Rezervasyon } from '@/types/rezervasyon';

interface Driver {
  id: number;
  ad_soyad: string;
  telefon: string;
  lisans_no: string;
  aktif: boolean;
}

interface DriverSummary {
  driver: Driver;
  toplamIs: number;
  toplamTutar: number;
  isler: Rezervasyon[];
}

const PARA_BIRIMLERI = ['TL', 'EUR', 'USD', 'GBP'];

function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

function getMonthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function SoforHesapPage() {
  useAuthGuard();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [rezervasyonlar, setRezervasyonlar] = useState<Rezervasyon[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');

  const [seciliSofor, setSeciliSofor] = useState<DriverSummary | null>(null);
  const [hedefPB, setHedefPB] = useState('TL');
  const [tarihBas, setTarihBas] = useState('');
  const [tarihBit, setTarihBit] = useState('');
  const [donemBtn, setDonemBtn] = useState('');
  const [odemeYukleniyor, setOdemeYukleniyor] = useState<number | null>(null);

  const { kurlar, yukleniyor: kurlarYukleniyor, hata: kurlarHata, cevir, format } = useDovizKurlari();

  const fetchData = useCallback(async () => {
    setYukleniyor(true);
    const [dRes, rRes] = await Promise.all([
      supabase.from('drivers').select('*').eq('aktif', true).order('ad_soyad'),
      supabase.from('reservations').select('*').order('gelis_tarihi', { ascending: false }),
    ]);
    if (dRes.error) setHata(dRes.error.message);
    else setDrivers(dRes.data || []);
    if (rRes.error) setHata(rRes.error.message);
    else setRezervasyonlar((rRes.data as Rezervasyon[]) || []);
    setYukleniyor(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summaries = useMemo(() => {
    const list: DriverSummary[] = drivers.map(d => {
      const isler = rezervasyonlar.filter(r => r.sofor === d.ad_soyad && r.durum !== 'İptal Edildi');
      const toplamTutar = isler.reduce((s, r) => {
        const tutar = Number(r.ucret) || 0;
        return s + cevir(tutar, r.para_birimi, 'TL');
      }, 0);
      return {
        driver: d,
        toplamIs: isler.length,
        toplamTutar,
        isler,
      };
    });
    return list.sort((a, b) => b.toplamTutar - a.toplamTutar);
  }, [drivers, rezervasyonlar, cevir]);

  const setHizliDonem = (tip: string) => {
    const today = getTodayStr();
    if (tip === 'bugun') {
      setTarihBas(today); setTarihBit(today);
    } else if (tip === 'ay') {
      setTarihBas(getMonthStartStr()); setTarihBit(today);
    } else {
      setTarihBas(''); setTarihBit('');
    }
    setDonemBtn(tip);
  };

  const filteredIsler = useMemo(() => {
    if (!seciliSofor) return [];
    let list = [...seciliSofor.isler];
    if (tarihBas) list = list.filter(r => r.gelis_tarihi >= tarihBas);
    if (tarihBit) list = list.filter(r => r.gelis_tarihi <= tarihBit);
    return list;
  }, [seciliSofor, tarihBas, tarihBit]);

  const toplamCevrilmis = useMemo(() => {
    return filteredIsler.reduce((s, r) => {
      const tutar = Number(r.ucret) || 0;
      return s + cevir(tutar, r.para_birimi, hedefPB);
    }, 0);
  }, [filteredIsler, hedefPB, cevir]);

  const odenenOzet = useMemo(() => {
    const odenen = filteredIsler.filter(r => r.sofor_odenme);
    const odenmemis = filteredIsler.filter(r => !r.sofor_odenme);
    const odenenTutar = odenen.reduce((s, r) => s + cevir(Number(r.ucret) || 0, r.para_birimi, hedefPB), 0);
    const odenmemisTutar = odenmemis.reduce((s, r) => s + cevir(Number(r.ucret) || 0, r.para_birimi, hedefPB), 0);
    return { odenen: odenen.length, odenmemis: odenmemis.length, odenenTutar, odenmemisTutar };
  }, [filteredIsler, hedefPB, cevir]);

  const durumRenk = (toplam: number) => {
    if (toplam === 0) return 'text-gray-500';
    if (toplam > 10000) return 'text-emerald-400';
    if (toplam > 5000) return 'text-vip-gold';
    return 'text-white';
  };

  const togglePayment = async (id: number, mevcut: boolean) => {
    setOdemeYukleniyor(id);
    const { data, error } = await supabase.functions.invoke('manage-reservations', {
      body: { action: 'updatePayment', id, data: { sofor_odenme: !mevcut } },
    });
    if (error || !data?.success) {
      setHata('Ödeme durumu güncellenemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
    } else {
      setRezervasyonlar(prev => prev.map(r => r.id === id ? { ...r, sofor_odenme: !mevcut } : r));
      if (seciliSofor) {
        setSeciliSofor({
          ...seciliSofor,
          isler: seciliSofor.isler.map(r => r.id === id ? { ...r, sofor_odenme: !mevcut } : r),
        });
      }
    }
    setOdemeYukleniyor(null);
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-display font-bold text-white">Şoför Hesap</h2>
        <p className="text-sm text-gray-400 mt-1">
          {yukleniyor ? 'Yükleniyor...' : `${summaries.length} aktif şoför · ${rezervasyonlar.filter(r => r.sofor && r.durum !== 'İptal Edildi').length} atanmış iş`}
        </p>
      </div>

      {hata && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-red-400"><i className="ri-error-warning-line mr-2"></i>{hata}</div>}
      {kurlarHata && <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-yellow-400"><i className="ri-alert-line mr-2"></i>Döviz kurları alınamadı: {kurlarHata}</div>}

      {/* Döviz Bilgisi */}
      {kurlar && (
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-gray-400">
          <span className="bg-vip-dark border border-vip-gold/10 rounded-md px-3 py-1.5">
            <i className="ri-exchange-line text-vip-gold mr-1"></i>
            EUR &rarr; {kurlar.rates.TRY ? `₺${kurlar.rates.TRY.toFixed(2)}` : '-'} ·
            $ {kurlar.rates.USD ? `${kurlar.rates.USD.toFixed(2)}` : '-'} ·
            £ {kurlar.rates.GBP ? `${kurlar.rates.GBP.toFixed(2)}` : '-'}
          </span>
          <span className="text-gray-500">{kurlar.tarih}</span>
        </div>
      )}

      {/* Şoför Kartları */}
      {yukleniyor ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 animate-pulse">
              <div className="h-4 bg-white/5 rounded w-3/4 mb-3"></div>
              <div className="h-8 bg-white/5 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-white/5 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {summaries.map((s) => (
            <button
              key={s.driver.id}
              onClick={() => { setSeciliSofor(s); setDonemBtn(''); setTarihBas(''); setTarihBit(''); }}
              className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 hover:border-vip-gold/30 transition-all text-left group cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center text-vip-gold font-semibold text-sm flex-shrink-0">
                  {s.driver.ad_soyad.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white truncate group-hover:text-vip-gold transition-colors">{s.driver.ad_soyad}</p>
                  <p className="text-xs text-gray-500">{s.driver.telefon}</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Toplam İş</p>
                  <p className="text-lg font-bold text-white">{s.toplamIs}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 mb-0.5">Toplam Tutar</p>
                  <p className={`text-lg font-bold ${durumRenk(s.toplamTutar)}`}>
                    {s.toplamTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                  </p>
                </div>
              </div>
              {s.toplamIs > 0 && (
                <p className="text-[10px] text-gray-500 mt-3">
                  <i className="ri-arrow-right-line mr-1"></i>Detaylı görmek için tıklayın
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      {summaries.length === 0 && !yukleniyor && (
        <div className="text-center py-12 text-gray-500">
          <i className="ri-steering-line text-4xl mb-3 block"></i>
          <p className="text-sm">Aktif şoför bulunamadı.</p>
          <p className="text-xs mt-1">Ayarlar &gt; Şoförler bölümünden yeni şoför ekleyebilirsiniz.</p>
        </div>
      )}

      {/* Detay Modal */}
      {seciliSofor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-vip-dark border border-vip-gold/20 rounded-xl w-full max-w-4xl mt-8 mb-8 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-vip-gold/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center text-vip-gold font-semibold text-sm flex-shrink-0">
                  {seciliSofor.driver.ad_soyad.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">{seciliSofor.driver.ad_soyad}</h3>
                  <p className="text-xs text-gray-500">{seciliSofor.driver.telefon} {seciliSofor.driver.lisans_no && `· Lisans: ${seciliSofor.driver.lisans_no}`}</p>
                </div>
              </div>
              <button onClick={() => setSeciliSofor(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Controls */}
            <div className="p-5 border-b border-vip-gold/10 space-y-3">
              {/* Period + Currency */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Quick Period */}
                <div className="flex gap-1 bg-vip-black border border-vip-gold/10 rounded-lg p-1">
                  {[
                    { key: 'tumu', label: 'Tümü' },
                    { key: 'bugun', label: 'Bugün' },
                    { key: 'ay', label: 'Bu Ay' },
                  ].map(({ key, label }) => (
                    <button key={key} onClick={() => setHizliDonem(key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all whitespace-nowrap ${donemBtn === key ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Date Range */}
                <div className="flex items-center gap-2">
                  <input type="date" value={tarihBas} onChange={e => { setTarihBas(e.target.value); setDonemBtn(''); }}
                    className="bg-vip-black border border-vip-gold/15 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:border-vip-gold/40" />
                  <span className="text-gray-500 text-xs">—</span>
                  <input type="date" value={tarihBit} onChange={e => { setTarihBit(e.target.value); setDonemBtn(''); }}
                    className="bg-vip-black border border-vip-gold/15 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:border-vip-gold/40" />
                </div>
                {/* Currency Selector */}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-gray-500">Para Birimi:</span>
                  <div className="flex gap-1 bg-vip-black border border-vip-gold/10 rounded-lg p-1">
                    {PARA_BIRIMLERI.map(pb => (
                      <button key={pb} onClick={() => setHedefPB(pb)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${hedefPB === pb ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
                        {pb}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Total + Payment Summary */}
              <div className="space-y-3">
                <div className="flex items-center gap-4 bg-vip-black/50 rounded-lg p-4 border border-vip-gold/10">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Seçili Dönem İş Sayısı</p>
                    <p className="text-xl font-bold text-white">{filteredIsler.length}</p>
                  </div>
                  <div className="w-px h-10 bg-vip-gold/10"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Şoförden Alınacak Toplam</p>
                    <p className="text-xl font-bold text-vip-gold">{format(toplamCevrilmis, hedefPB)}</p>
                  </div>
                  <div className="w-px h-10 bg-vip-gold/10"></div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Ortalama İş Başı</p>
                    <p className="text-xl font-bold text-white">
                      {filteredIsler.length > 0 ? format(toplamCevrilmis / filteredIsler.length, hedefPB) : format(0, hedefPB)}
                    </p>
                  </div>
                </div>

                {/* Ödenen / Ödenmemiş Özet */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center">
                        <i className="ri-check-double-line text-emerald-400 text-xs"></i>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">Ödenen İş</p>
                        <p className="text-sm font-bold text-emerald-400">{odenenOzet.odenen} iş</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-emerald-400">{format(odenenOzet.odenenTutar, hedefPB)}</p>
                  </div>
                  <div className="flex-1 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-amber-500/15 flex items-center justify-center">
                        <i className="ri-time-line text-amber-400 text-xs"></i>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400">Ödenmemiş İş</p>
                        <p className="text-sm font-bold text-amber-400">{odenenOzet.odenmemis} iş</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-amber-400">{format(odenenOzet.odenmemisTutar, hedefPB)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="p-5">
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-vip-dark z-10">
                    <tr className="border-b border-vip-gold/10">
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Tarih</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rez. No</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Müşteri</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Rota</th>
                      <th className="text-left py-2.5 px-3 text-gray-400 font-medium">Araç / Plaka</th>
                      <th className="text-right py-2.5 px-3 text-gray-400 font-medium">Orjinal</th>
                      <th className="text-right py-2.5 px-3 text-gray-400 font-medium">{hedefPB}</th>
                      <th className="text-center py-2.5 px-3 text-gray-400 font-medium">Durum</th>
                      <th className="text-center py-2.5 px-3 text-gray-400 font-medium">Ödeme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIsler.map(r => {
                      const cevrilmis = cevir(Number(r.ucret) || 0, r.para_birimi, hedefPB);
                      const odenmis = !!r.sofor_odenme;
                      return (
                        <tr key={r.id} className={`border-b border-vip-gold/5 hover:bg-white/5 transition-colors ${odenmis ? 'opacity-50' : ''}`}>
                          <td className="py-2.5 px-3 text-gray-300 whitespace-nowrap">{r.gelis_tarihi}</td>
                          <td className={`py-2.5 px-3 font-mono ${odenmis ? 'text-gray-500 line-through' : 'text-vip-gold'}`}>{r.rezervasyon_no}</td>
                          <td className={`py-2.5 px-3 ${odenmis ? 'text-gray-500' : 'text-white'}`}>{r.musteri_adi}</td>
                          <td className={`py-2.5 px-3 ${odenmis ? 'text-gray-500' : 'text-gray-400'}`}>{r.alinis_yeri} {r.birak_yeri !== 'Yok' && r.birak_yeri ? `→ ${r.birak_yeri}` : ''}</td>
                          <td className={`py-2.5 px-3 ${odenmis ? 'text-gray-500' : 'text-gray-400'}`}>{r.atanan_arac || '-'} {r.atanan_plaka ? `(${r.atanan_plaka})` : ''}</td>
                          <td className={`py-2.5 px-3 text-right ${odenmis ? 'text-gray-500' : 'text-gray-400'}`}>{r.ucret} {r.para_birimi}</td>
                          <td className={`py-2.5 px-3 text-right font-semibold ${odenmis ? 'text-gray-500 line-through' : 'text-vip-gold'}`}>{format(cevrilmis, hedefPB)}</td>
                          <td className="py-2.5 px-3 text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${r.durum === 'Transfer Tamamlandı' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : r.durum === 'İptal Edildi' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-vip-gold/10 text-vip-gold border-vip-gold/20'}`}>
                              {r.durum}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              onClick={() => togglePayment(r.id, odenmis)}
                              disabled={odemeYukleniyor === r.id}
                              className={`w-6 h-6 rounded border flex items-center justify-center transition-all cursor-pointer ${
                                odenmis
                                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/30'
                                  : 'bg-transparent border-gray-600 hover:border-vip-gold/40 hover:bg-vip-gold/10'
                              }`}
                              title={odenmis ? 'Ödeme yapıldı (geri al)' : 'Ödeme yapıldı işaretle'}
                            >
                              {odemeYukleniyor === r.id ? (
                                <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                              ) : odenmis ? (
                                <i className="ri-check-line text-[10px]"></i>
                              ) : (
                                <i className="ri-check-line text-[10px] text-transparent"></i>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredIsler.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-gray-500 text-sm">
                          <i className="ri-inbox-line text-2xl mb-2 block"></i>
                          Seçili dönemde iş kaydı bulunamadı.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-vip-dark z-10">
                    <tr className="border-t border-vip-gold/20">
                      <td colSpan={6} className="py-3 px-3 text-right text-xs text-gray-400 font-medium">TOPLAM</td>
                      <td className="py-3 px-3 text-right text-vip-gold font-bold text-sm">{format(toplamCevrilmis, hedefPB)}</td>
                      <td className="py-3 px-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t border-vip-gold/10">
              <div className="text-[10px] text-gray-500">
                {kurlarYukleniyor ? 'Kurlar yükleniyor...' : kurlar ? `Kur: ${kurlar.tarih}` : 'Kur verisi yok'}
              </div>
              <button onClick={() => setSeciliSofor(null)} className="px-4 py-2 bg-vip-gold/15 text-vip-gold text-xs font-medium rounded-md border border-vip-gold/20 hover:bg-vip-gold/25 transition-colors whitespace-nowrap">
                <i className="ri-close-line mr-1"></i>Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}